import React, { useCallback } from 'react';

import * as Sentry from '@sentry/react-native';

import { MixedItem, RBParsedRail } from 'store/railsBuilder/reducer.types';

import { getCollectionData as requestCollectionData } from 'services/data-manager/index';
import parseRailSection from 'services/data-manager/parsing/parseRailSection';
import {
    CdvrRecording,
    ContentType,
    EventItemWithServiceInfo,
} from 'services/data-manager/types';

import { CollectionItemsList } from 'screens/Collection/types';
import {
    OnSetStripeLoadingStatus,
    RailLoadingStatus,
} from 'screens/Modular/types';

type Params = {
    rail: RBParsedRail | undefined;
    templateId: string | undefined;
    limit: number;
    setHasNext: React.Dispatch<React.SetStateAction<boolean>>;
    setData: React.Dispatch<React.SetStateAction<CollectionItemsList>>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    setTotalItems: React.Dispatch<React.SetStateAction<number>>;
    onSetStripeLoadingStatus?: OnSetStripeLoadingStatus;
};

export const INITIAL_PAGE = 1;

function useLoadGenericRail({
    rail,
    templateId,
    limit,
    setHasNext,
    setData,
    setPage,
    setTotalItems,
    onSetStripeLoadingStatus,
}: Params) {
    return useCallback(
        async (page: number) => {
            if (rail && templateId) {
                // get data by `/templateviews/${template.id}/rails/${rail.id}` API
                try {
                    onSetStripeLoadingStatus?.(
                        rail.id,
                        RailLoadingStatus.LOADING,
                    );

                    const railData = await requestCollectionData({
                        templateId,
                        rail,
                        limit,
                        page,
                    });

                    let nullPositions: number[] = [];
                    const filteredSections = railData.sections.filter(
                        (section, index) => {
                            if (!section) {
                                nullPositions = [...nullPositions, index];
                            }

                            return !!section;
                        },
                    );

                    if (nullPositions.length > 0) {
                        Sentry.captureException({
                            name: 'Null positions in rail sections',
                            info: {
                                code: 'A014',
                                message: 'Invalid item within rail',
                                railId: rail.id,
                                railName: rail.name,
                                nullPositions: JSON.stringify(nullPositions),
                            },
                        });
                    }

                    let resultSections: CollectionItemsList;

                    // Kids Channels and NowNext stripes will be built based on the /rails/${id} response data
                    if (
                        !rail.properties.MDS_Call &&
                        rail.properties.ContentType === ContentType.ChannelItem
                    ) {
                        resultSections = filteredSections.reduce<
                            EventItemWithServiceInfo[]
                        >(
                            (acc, section) => [
                                ...acc,
                                ...(section.contents as EventItemWithServiceInfo[]),
                            ],
                            [],
                        );
                    } else {
                        resultSections = filteredSections.reduce<
                            (MixedItem | CdvrRecording)[]
                        >(
                            (acc, section) => [
                                ...acc,
                                ...parseRailSection(section),
                            ],
                            [],
                        );
                    }

                    setHasNext(railData.hasNext);
                    // @ts-ignore
                    setData((prevData) => {
                        if (page > INITIAL_PAGE) {
                            return [...prevData, ...resultSections];
                        }

                        return resultSections;
                    });
                    setPage(page);
                    setTotalItems((prevTotalItems) => {
                        // As far as /rail/${id} response doesn't have "totalItems" information,
                        // app will increase received items amount by 1 on the initial page if "hasNext" is true,
                        // and decrease total items value by 1 when "hasNext" will become false for page > 0.
                        if (page === INITIAL_PAGE && railData.hasNext) {
                            return resultSections.length + 1;
                        }

                        if (page === INITIAL_PAGE && !railData.hasNext) {
                            return resultSections.length;
                        }

                        if (page > INITIAL_PAGE && !railData.hasNext) {
                            return prevTotalItems + resultSections.length - 1;
                        }

                        return prevTotalItems + resultSections.length;
                    });

                    if (!resultSections.length) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.EMPTY,
                        );
                    } else {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADED,
                        );
                    }
                } catch (e) {
                    onSetStripeLoadingStatus?.(
                        rail.id,
                        RailLoadingStatus.FAILURE,
                    );
                }
            }
        },
        [
            rail,
            templateId,
            onSetStripeLoadingStatus,
            limit,
            setHasNext,
            setData,
        ],
    );
}

export default useLoadGenericRail;
